import { AxiosError } from 'axios';

/**
 * Represents a parsed RFC 7807 Problem Details response from the backend.
 * See: src/bds/common/filters/problem-details.filter.ts
 */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance?: string;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;
  readonly isNetworkError: boolean;

  constructor(message: string, status: number, problem: ProblemDetails | null, isNetworkError = false) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.isNetworkError = isNetworkError;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function coerceDetail(
  raw: string | string[] | undefined,
  fallback: string
): string {
  if (Array.isArray(raw)) {
    return raw.filter((item) => typeof item === 'string').join('; ') || fallback;
  }
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw;
  }
  return fallback;
}

export function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 0;
    const data: unknown = error.response?.data;
    let problem: ProblemDetails | null = null;

    if (data && typeof data === 'object') {
      const maybe = data as Partial<ProblemDetails> & {
        message?: string | string[];
        error?: string;
        statusCode?: number;
      };
      const baseStatus =
        typeof maybe.status === 'number'
          ? maybe.status
          : typeof maybe.statusCode === 'number'
            ? maybe.statusCode
            : status;

      const hasProblemShape = typeof maybe.title === 'string' || typeof maybe.detail === 'string';
      const hasNestShape =
        typeof maybe.message === 'string' ||
        Array.isArray(maybe.message) ||
        typeof maybe.error === 'string';

      if (hasProblemShape) {
        problem = {
          type: typeof maybe.type === 'string' ? maybe.type : 'about:blank',
          title: maybe.title ?? 'Error',
          status: baseStatus,
          detail: coerceDetail(maybe.detail ?? maybe.message, error.message),
          instance: maybe.instance,
          errors: maybe.errors
        };
      } else if (hasNestShape) {
        problem = {
          type: 'about:blank',
          title: maybe.error ?? 'Error',
          status: baseStatus,
          detail: coerceDetail(maybe.message, maybe.error ?? error.message)
        };
      }
    } else if (typeof data === 'string' && data.trim()) {
      problem = {
        type: 'about:blank',
        title: 'Error',
        status,
        detail: data.trim()
      };
    }

    const networkError = !error.response;
    const message =
      problem?.detail ??
      problem?.title ??
      (networkError ? 'Unable to reach the server. Please check your connection.' : error.message);
    return new ApiError(message, status, problem, networkError);
  }
  if (error instanceof Error) {
    return new ApiError(error.message, 0, null);
  }
  return new ApiError('Unknown error', 0, null);
}

export function formatFieldErrors(problem: ProblemDetails | null): string[] {
  if (!problem?.errors) {
    return [];
  }
  return Object.entries(problem.errors).flatMap(([field, msgs]) =>
    msgs.map((msg) => `${field}: ${msg}`)
  );
}
